import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, ArrowDown, Star, Check } from 'lucide-react';
import { UrgencyBar } from '../components/UrgencyBar';
import { PurchasePopup } from '../components/PurchasePopup';
import { ComboCard } from '../components/ComboCard';
import { TestimonialCard } from '../components/TestimonialCard';
// Image URLs
const MAIN_PRODUCT_IMG = 'https://cdn.magicpatterns.com/uploads/kYz1HhWECJVSPeFtyM737Y/ChatGPT_Image_27_ene_2026,_02_13_39.png';
const BONUS_1_IMG = 'https://cdn.magicpatterns.com/uploads/2gbH4DjCGn44jQsDErxfnV/ChatGPT_Image_27_ene_2026,_02_44_14.png';
const BONUS_2_IMG = 'https://cdn.magicpatterns.com/uploads/tmmSwC9h6AFBXSfzHkXd4T/ChatGPT_Image_27_ene_2026,_02_44_10.png';
const BONUS_3_IMG = 'https://cdn.magicpatterns.com/uploads/35g3YGsWzEsLy8oBUGfM3c/ChatGPT_Image_27_ene_2026,_02_44_04.png';
const MERCADO_LIBRE_IMG = 'https://cdn.magicpatterns.com/uploads/aSqE9gyQNM7hvR9VAB7BpK/ML.jpg';
// Combo Images
const COMBO_1_IMG = 'https://cdn.magicpatterns.com/uploads/s4MGB9kGsyNMzqDLKEUAnv/ChatGPT_Image_27_ene_2026,_02_13_39.png';
const COMBO_2_IMG = 'https://cdn.magicpatterns.com/uploads/mj5B8BvRdTCnLH5qA38vf2/ChatGPT_Image_27_ene_2026,_13_36_38.png';
const COMBO_3_IMG = 'https://cdn.magicpatterns.com/uploads/piYJqddWjF6TxsW87ub5g3/ChatGPT_Image_27_ene_2026,_13_45_30.png';
export function SalesPage() {
  const checkoutRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    document.title = 'MANUAL DE LENGUAJE CORPORAL';
  }, []);
  const scrollToCheckout = () => {
    checkoutRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const handleBuy = (comboNumber: 1 | 2 | 3) => {
    const checkoutLinks = {
      1: 'https://checkout3.rosetadigital.com/cart/45956744675513:1',
      2: 'https://checkout3.rosetadigital.com/cart/45959149551801:1',
      3: 'https://checkout3.rosetadigital.com/cart/45959188447417:1'
    };
    window.location.href = checkoutLinks[comboNumber];
  };
  return <div className="min-h-screen bg-black text-gray-200 font-sans selection:bg-yellow-500 selection:text-black" data-id="element-46">
      <UrgencyBar data-id="element-47" />
      <PurchasePopup data-id="element-48" />

      {/* Hero Section */}
      <header className="relative pt-12 pb-20 px-4 overflow-hidden" data-id="element-49">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800/20 via-black to-black z-0" data-id="element-50"></div>

        <div className="max-w-4xl mx-auto relative z-10 text-center" data-id="element-51">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-52">
            <h2 className="text-yellow-500 font-bold uppercase tracking-widest text-sm mb-4" data-id="element-53">
              Nadie Te Preparó Para Esto…
            </h2>
            <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight" data-id="element-54">
              En el mundo real, no gana el que habla más.
              <br data-id="element-55" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600" data-id="element-56">
                Gana el que entiende mejor.
              </span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-10" data-id="element-57">
              Descubre el sistema de 3 capas para leer personas, proyectar
              autoridad y defenderte de la manipulación.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          delay: 0.3,
          duration: 0.6
        }} className="mb-12 relative inline-block group cursor-pointer" onClick={scrollToCheckout} data-id="element-58">
            <div className="absolute -inset-1 bg-gradient-to-r from-yellow-600 to-yellow-400 rounded-lg blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200" data-id="element-59"></div>
            <img src={MAIN_PRODUCT_IMG} alt="Lenguaje Corporal Libro Principal" className="relative rounded-lg shadow-2xl max-w-full h-auto md:max-h-[500px] mx-auto transform group-hover:scale-[1.01] transition-transform duration-300" data-id="element-60" />
          </motion.div>

          <motion.button whileHover={{
          scale: 1.05
        }} whileTap={{
          scale: 0.95
        }} onClick={scrollToCheckout} className="bg-yellow-500 hover:bg-yellow-400 text-black font-bold text-xl py-4 px-12 rounded-full shadow-[0_0_20px_rgba(253,185,19,0.4)] uppercase tracking-wide flex items-center gap-2 mx-auto" data-id="element-61">
            Quiero Dominar Mi Entorno <ArrowDown className="w-6 h-6" data-id="element-62" />
          </motion.button>
        </div>
      </header>

      {/* Sales Letter Content */}
      <section className="py-16 px-4 bg-zinc-900/30" data-id="element-63">
        <div className="max-w-2xl mx-auto prose prose-invert prose-lg" data-id="element-64">
          <p className="text-xl font-medium text-white" data-id="element-65">
            Desde chico te enseñaron a hablar.
          </p>
          <p data-id="element-66">A ser educado. A confiar. A ser correcto.</p>
          <p className="font-bold text-white text-2xl border-l-4 border-yellow-500 pl-4 my-8" data-id="element-67">
            Pero nunca te enseñaron a leer personas.
          </p>
          <p data-id="element-68">Y eso… lo pagaste caro.</p>
          <p data-id="element-69">
            Tal vez confiando en quien no debías. Tal vez callando cuando tenías
            que imponerte. Tal vez eligiendo mal. Tal vez perdiendo
            oportunidades.
          </p>

          <h3 className="text-3xl font-bold text-white mt-12 mb-6" data-id="element-70">
            El Problema No Sos Vos
          </h3>
          <p data-id="element-71">Durante años pensé que era "mala suerte".</p>
          <p data-id="element-72">
            Que siempre me tocaban personas complicadas. Que siempre llegaba
            tarde a darme cuenta. Que siempre reaccionaba después.
          </p>
          <p data-id="element-73">Hasta que entendí algo:</p>
          <p className="text-yellow-400 font-bold text-xl" data-id="element-74">
            No veía lo que estaba frente a mí.
          </p>
          <p data-id="element-75">Señales. Gestos. Cambios. Microexpresiones.</p>
          <p data-id="element-76">Todo estaba ahí. Yo no sabía leerlo.</p>

          <h3 className="text-3xl font-bold text-white mt-12 mb-6" data-id="element-77">
            Probé Todo… Y Nada Funcionó
          </h3>
          <p data-id="element-78">
            Videos en YouTube. Frases motivacionales. Libros densos. Cursos
            carísimos.
          </p>
          <p data-id="element-79">Nada conectaba. Todo era teoría. Nada era sistema.</p>
          <p data-id="element-80">Hasta que empecé a unir piezas.</p>

          <div className="bg-gray-900 p-8 rounded-xl border border-gray-800 my-12" data-id="element-81">
            <h3 className="text-2xl font-bold text-yellow-400 mt-0 mb-4" data-id="element-82">
              El Descubrimiento
            </h3>
            <p className="mb-4" data-id="element-83">
              No necesitás más información. Necesitás estructura.
            </p>
            <p className="font-bold text-white mb-2" data-id="element-84">Tres capas:</p>
            <div className="flex gap-4 mb-6 text-white font-bold" data-id="element-85">
              <span className="bg-gray-800 px-3 py-1 rounded" data-id="element-86">1️⃣ Leer</span>
              <span className="bg-gray-800 px-3 py-1 rounded" data-id="element-87">
                2️⃣ Proyectar
              </span>
              <span className="bg-gray-800 px-3 py-1 rounded" data-id="element-88">
                3️⃣ Defenderte
              </span>
            </div>
            <p className="m-0" data-id="element-89">Si falta una, perdés.</p>
          </div>

          <h3 className="text-3xl font-bold text-white mt-12 mb-6" data-id="element-90">
            Por eso nació este manual completo de lenguaje corporal
          </h3>
          <p data-id="element-91">Un sistema completo para:</p>
          <ul className="list-none pl-0 space-y-2" data-id="element-92">
            <li className="flex items-center gap-2" data-id="element-93">
              <CheckCircleIcon data-id="element-94" /> Entender personas
            </li>
            <li className="flex items-center gap-2" data-id="element-95">
              <CheckCircleIcon data-id="element-96" /> Impactar sin forzar
            </li>
            <li className="flex items-center gap-2" data-id="element-97">
              <CheckCircleIcon data-id="element-98" /> Evitar manipulaciones
            </li>
          </ul>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-black" data-id="element-99">
        <div className="max-w-4xl mx-auto text-center" data-id="element-100">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12" data-id="element-101">
            Qué Va a Cambiar en Vos
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-102">
            {[{
            title: 'Notar cosas antes',
            icon: '👁️'
          }, {
            title: 'Dudar menos',
            icon: '⚡'
          }, {
            title: 'Elegir mejor',
            icon: '🎯'
          }, {
            title: 'Decidir con claridad',
            icon: '🧠'
          }, {
            title: 'Ser respetado',
            icon: '👑'
          }, {
            title: 'Sin fingir',
            icon: '🎭'
          }].map((item, idx) => <div key={idx} className="bg-gray-900 p-6 rounded-lg border border-gray-800 hover:border-yellow-500/50 transition-colors" data-id="element-103">
                <div className="text-4xl mb-4" data-id="element-104">{item.icon}</div>
                <h3 className="text-xl font-bold text-white" data-id="element-105">{item.title}</h3>
              </div>)}
          </div>

          <div className="bg-gradient-to-r from-gray-900 to-black border border-gray-800 p-8 rounded-xl max-w-2xl mx-auto" data-id="element-106">
            <h3 className="text-2xl font-bold text-white mb-4" data-id="element-107">
              Esto No Es Motivación
            </h3>
            <p className="text-gray-300 text-lg" data-id="element-108">
              Es entrenamiento mental. Como ir al gimnasio, pero para tu vida
              social.
            </p>
          </div>
        </div>
      </section>

      {/* Bonuses Section */}
      <section className="py-20 px-4 bg-zinc-900" data-id="element-109">
        <div className="max-w-6xl mx-auto" data-id="element-110">
          <div className="text-center mb-16" data-id="element-111">
            <span className="bg-red-600 text-white px-4 py-1 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-112">
              Gratis por hoy
            </span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-4 mb-6" data-id="element-113">
              Tus 3 Bonus Exclusivos
            </h2>
            <p className="text-gray-400 text-xl" data-id="element-114">
              Complementos perfectos para acelerar tus resultados
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-115">
            <BonusCard image={BONUS_1_IMG} title="BONUS 1: Lectura Rápida" subtitle="En la Vida Real" description="Aprende a escanear cualquier situación social en segundos y saber quién es quién." data-id="element-116" />
            <BonusCard image={BONUS_2_IMG} title="BONUS 2: Influencia" subtitle="Persuasión Invisible" description="Técnicas sutiles para que tus ideas sean aceptadas sin resistencia." data-id="element-117" />
            <BonusCard image={BONUS_3_IMG} title="BONUS 3: Detector" subtitle="Mentiras y Manipulación" description="Descubre las microexpresiones que delatan el engaño al instante." data-id="element-118" />
          </div>
        </div>
      </section>

      {/* Trust Badge Before Checkout */}
      <section className="py-8 px-4 bg-black" data-id="element-119">
        <div className="max-w-4xl mx-auto" data-id="element-120">
          <div className="flex flex-col items-center justify-center gap-4" data-id="element-121">
            <p className="text-gray-400 text-sm uppercase tracking-widest" data-id="element-122">
              Pago 100% seguro procesado por
            </p>
            <img src={MERCADO_LIBRE_IMG} alt="Mercado Pago" className="h-12 object-contain bg-white px-4 py-2 rounded-lg" data-id="element-123" />
          </div>
        </div>
      </section>

      {/* Pricing / Checkout Section */}
      <section ref={checkoutRef} className="py-16 px-4 bg-gradient-to-b from-black to-gray-900 relative" data-id="element-124">
        <div className="max-w-6xl mx-auto" data-id="element-125">
          <div className="text-center mb-12" data-id="element-126">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-127">
              Elige Tu Nivel de Dominio
            </h2>
            <p className="text-xl text-gray-400" data-id="element-128">
              Descarga inmediata. Acceso de por vida.
            </p>

            <div className="flex items-center justify-center gap-2 mt-6 text-red-500 font-bold bg-red-500/10 inline-block px-4 py-2 rounded-lg mx-auto" data-id="element-129">
              <span className="animate-pulse" data-id="element-130">●</span> Solo quedan 7 copias con
              estos bonus
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch max-w-5xl mx-auto" data-id="element-131">
            <ComboCard title="Combo 1 – Lector Humano" price="19.990" description="Ideal si querés aprender a leer gestos, actitudes e intenciones en situaciones reales." image={COMBO_1_IMG} features={['Manual: LENGUAJE CORPORAL', 'BONUS 1: Lectura Rápida (Gratis)', 'BONUS 2: Influencia y Persuasión (Gratis)', 'BONUS 3: Detector de Mentiras (Gratis)']} onBuy={() => handleBuy(1)} data-id="element-132" />

            <ComboCard title="Combo 2 – Influencia Total" price="29.990" isRecommended={true} description="Pensado para quienes, además de leer personas, quieren proyectar seguridad, autoridad y confianza." image={COMBO_2_IMG} features={['TODO el Combo 1 (Bonus incluidos gratis)', 'Manual: Carisma y Presencia']} onBuy={() => handleBuy(2)} data-id="element-133" />

            <ComboCard title="Combo 3 – Dominio Mental" price="39.990" isPremium={true} description="Ideal si querés avanzar un paso más y protegerte de manipulaciones, engaños y relaciones tóxicas." image={COMBO_3_IMG} features={['TODO el Combo 1 y 2 (Bonus incluidos gratis)', 'Manual: Psicología Oscura']} onBuy={() => handleBuy(3)} data-id="element-134" />
          </div>

          <div className="text-center mt-8" data-id="element-135">
            <p className="text-gray-400 text-sm" data-id="element-136">
              Todos los manuales en formato{' '}
              <span className="font-bold text-white" data-id="element-137">PDF</span> de descarga
              inmediata
            </p>
          </div>

          {/* Trust Badges & Payment */}
          <div className="mt-16 text-center" data-id="element-138">
            <div className="flex flex-col items-center justify-center gap-6 mb-8" data-id="element-139">
              <p className="text-gray-400 text-sm uppercase tracking-widest" data-id="element-140">
                Pago 100% seguro procesado por
              </p>
              <img src={MERCADO_LIBRE_IMG} alt="Mercado Pago" className="h-12 object-contain bg-white px-4 py-2 rounded-lg" data-id="element-141" />
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-gray-500 text-sm" data-id="element-142">
              <div className="flex items-center gap-2" data-id="element-143">
                <Shield className="w-5 h-5" data-id="element-144" /> Garantía de satisfacción
              </div>
              <div className="flex items-center gap-2" data-id="element-145">
                <Lock className="w-5 h-5" data-id="element-146" /> Datos encriptados
              </div>
              <div className="flex items-center gap-2" data-id="element-147">
                <CreditCard className="w-5 h-5" data-id="element-148" /> Acceso inmediato
              </div>
            </div>

            <p className="mt-12 text-gray-500 max-w-2xl mx-auto" data-id="element-149">
              "Elegí el combo que mejor se adapte a tu objetivo actual. Todos
              los materiales son digitales y de acceso inmediato."
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-black" data-id="element-150">
        <div className="max-w-6xl mx-auto" data-id="element-151">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12" data-id="element-152">
            Lo Que Dicen Quienes Ya Lo Leyeron
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-153">
            <TestimonialCard name="Martín González" role="Vendedor, 34 años" content="Siempre sentía que perdía en las negociaciones. Con el Combo 2 entendí que mi cuerpo decía 'soy débil' antes de abrir la boca. Cambió mi carrera." data-id="element-154" />
            <TestimonialCard name="Laura Fernández" role="Gerente de RRHH, 41 años" content="El módulo de detección de mentiras es escalofriante. Ahora veo cosas en las entrevistas que antes pasaba por alto. Imprescindible." data-id="element-155" />
            <TestimonialCard name="Javier Ruiz" role="Emprendedor, 29 años" content="No es teoría aburrida. Lo leí a la tarde y a la noche ya estaba probando cosas en una cena. Funciona. Punto." data-id="element-156" />
            <TestimonialCard name="Sofía Méndez" role="Abogada, 38 años" content="La psicología oscura me abrió los ojos. Me di cuenta de cómo un colega me manipulaba sutilmente. Nunca más." data-id="element-157" />
            <TestimonialCard name="Diego Torres" role="Estudiante, 24 años" content="Era muy tímido y no sabía dónde poner las manos. El libro de carisma es oro puro. Me siento otra persona." data-id="element-158" />
          </div>
        </div>
      </section>
    </div>;
}
function CheckCircleIcon() {
  return <Check className="w-5 h-5 text-yellow-500 flex-shrink-0" data-id="element-159" />;
}
function BonusCard({
  image,
  title,
  subtitle,
  description
}: {
  image: string;
  title: string;
  subtitle: string;
  description: string;
}) {
  return <div className="bg-black border border-gray-800 rounded-xl overflow-hidden hover:border-gray-600 transition-colors group" data-id="element-160">
      <div className="h-72 overflow-hidden relative bg-gray-900 flex items-center justify-center" data-id="element-161">
        <img src={image} alt={title} className="w-full h-full object-contain transform group-hover:scale-105 transition-transform duration-500" data-id="element-162" />
      </div>
      <div className="p-6" data-id="element-163">
        <h3 className="text-yellow-500 font-bold text-sm uppercase mb-1" data-id="element-164">
          {title}
        </h3>
        <h4 className="text-white font-bold text-xl mb-3" data-id="element-165">{subtitle}</h4>
        <p className="text-gray-400 text-sm" data-id="element-166">{description}</p>
      </div>
    </div>;
}